import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DashboardTip } from '../../../services/dashboard.service';
import { KuboAvatarComponent } from '../../ui/kubo-avatar.component';
import { greeting, resolveDashboardImage } from './dashboard-view.util';

/**
 * Banner principal del negocio: saludo contextual, acciones y consejo útil.
 */
@Component({
  selector: 'app-dashboard-hero',
  standalone: true,
  imports: [CommonModule, KuboAvatarComponent],
  template: `
    <section class="hero" [class.is-loading]="loading">
      <img class="hero-bg" [src]="resolvedImage" [attr.alt]="businessName ? 'Espacio de ' + businessName : 'Espacio de trabajo'" (error)="onImageError($event)" />
      <div class="hero-body">
        <p class="hero-hello"><span class="hello-icon" aria-hidden="true"><i class="bi bi-person-check-fill"></i></span>{{ saludo }}<span *ngIf="firstName"> {{ firstName }}!</span></p>
        <h2>Tu negocio, siempre <span>en movimiento</span></h2>
        <p class="hero-copy">Administra tus espacios, revisa tus reservas y brinda una gran experiencia a cada cliente.</p>
        <div class="hero-actions">
          <button *ngIf="canCreateReservation" type="button" class="hero-btn primary" (click)="createReservation.emit()">
            <i class="bi bi-plus-lg" aria-hidden="true"></i> Crear reserva
          </button>
          <button *ngIf="canManageSpaces" type="button" class="hero-btn ghost" (click)="manageSpaces.emit()">
            <i class="bi bi-sliders" aria-hidden="true"></i> Administrar espacios
          </button>
        </div>
      </div>
      <aside class="hero-aside" *ngIf="tip">
        <section class="hero-tip" *ngIf="tip" aria-labelledby="hero-tip-title">
          <app-kubo-avatar class="hero-tip-kubo" mood="explicando"></app-kubo-avatar>
          <div class="hero-tip-copy">
            <p><i class="bi bi-lightbulb-fill" aria-hidden="true"></i> Consejo de Kubo</p>
            <strong id="hero-tip-title">{{ tip.text }}</strong>
            <button type="button" (click)="openTip.emit(tip.module)">{{ tip.action }} <i class="bi bi-arrow-right" aria-hidden="true"></i></button>
          </div>
        </section>
      </aside>
    </section>
  `,
  styles: [`
    :host { display: block; }
    .hero {
      position: relative;
      display: flex;
      align-items: flex-end;
      min-height: 226px;
      padding: 1.5rem 1.6rem;
      overflow: hidden;
      border: 1px solid var(--ts-border);
      border-radius: var(--ts-radius-lg);
      background: transparent;
      box-shadow: var(--ts-shadow-md);
      isolation: isolate;
    }
    .hero.is-loading .hero-bg { opacity: .4; }
    .hero-bg { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; z-index: -2; transition: opacity var(--ts-transition); }
    .hero-body { position: relative; max-width: min(620px, calc(100% - 330px)); color: #fff; }
    .hero-hello { display: flex; align-items: center; gap: .42rem; margin: 0 0 .4rem; font-size: .82rem; font-weight: 700; color: #fff; opacity: .98; }
    .hello-icon { display: inline-grid; place-items: center; flex: 0 0 auto; color: #fbbf24; border: 1px solid rgba(251,191,36,.45); background: rgba(251,191,36,.14); }
    .hello-icon { width: 25px; height: 25px; border-radius: 8px; font-size: .72rem; }
    .hero h2 { margin: 0 0 .5rem; color: #fff; font-family: "Plus Jakarta Sans", Inter, sans-serif; font-size: clamp(1.5rem, 2.4vw, 2.05rem); line-height: 1.12; letter-spacing: -.03em; text-shadow: 0 2px 14px rgba(0, 0, 0, .55); }
    .hero h2 span { color: #7cc4ff; }
    .hero-copy { margin: 0; max-width: 46ch; color: rgba(255,255,255,.94); font-size: .8rem; line-height: 1.5; text-shadow: 0 1px 10px rgba(0, 0, 0, .5); }
    .hero-actions { display: flex; flex-wrap: wrap; gap: .6rem; margin-top: 1.05rem; }
    .hero-btn { display: inline-flex; align-items: center; gap: .45rem; min-height: 42px; padding: 0 1.05rem; border-radius: 11px; border: 1px solid transparent; font: inherit; font-size: .78rem; font-weight: 750; cursor: pointer; transition: background var(--ts-transition), border-color var(--ts-transition), box-shadow var(--ts-transition); }
    .hero-btn.primary { color: #fff; background: linear-gradient(135deg, var(--ts-primary), var(--ts-indigo)); box-shadow: 0 10px 24px var(--ts-shadow-blue-500-a28); }
    /* Sin backdrop-filter (sobre un degradado no se notaba y lo dejaba en una
       capa gráfica aparte que al hacer scroll se despegaba del fondo). */
    .hero-btn.ghost { color: #fff; border-color: rgba(255,255,255,.4); background: rgba(255,255,255,.16); }
    .hero-btn.ghost:hover { background: rgba(255,255,255,.26); }
    .hero-aside { position: absolute; top: 1rem; right: 1rem; bottom: 1rem; display: flex; flex-direction: column; align-items: flex-end; justify-content: flex-end; gap: .65rem; width: min(300px, 35%); }
    .hero-tip { position: relative; box-sizing: border-box; display: grid; grid-template-columns: 58px minmax(0, 1fr); align-items: center; gap: .6rem; width: 100%; min-height: 112px; padding: .75rem .8rem .75rem .55rem; overflow: hidden; color: #fff; border: 1px solid rgba(191,219,254,.38); border-radius: 13px; background: rgba(25,58,90,.92); box-shadow: 0 8px 22px rgba(0,0,0,.18); text-shadow: 0 1px 6px rgba(0,0,0,.68); }
    .hero-tip p { display: flex; align-items: center; gap: .38rem; margin: 0 0 .35rem; color: #fff; font-size: .58rem; font-weight: 850; letter-spacing: .06em; text-transform: uppercase; }
    .hero-tip-kubo { width: 58px; height: 58px; }
    .hero-tip p i { color: #fcd34d; }
    .hero-tip strong { display: block; color: #fff; font-size: .7rem; font-weight: 650; line-height: 1.4; text-shadow: 0 1px 7px rgba(0,0,0,.7); }
    .hero-tip button { display: inline-flex; align-items: center; gap: .35rem; margin-top: .55rem; padding: 0; color: #bfdbfe; border: 0; background: transparent; font: inherit; font-size: .63rem; font-weight: 800; cursor: pointer; }
    .hero-tip button:hover { color: #fff; text-decoration: underline; }
    @media (max-width: 960px) {
      .hero { min-height: 220px; padding: 1.25rem; }
      .hero-bg { object-position: center; }
      .hero-body { max-width: 100%; }
      .hero-aside { position: static; width: 100%; margin-top: 1rem; align-items: stretch; }
    }
    @media (max-width: 600px) {
      .hero { flex-direction: column; align-items: stretch; min-height: 260px; padding: 1.05rem; }
      .hero-bg { object-position: 58% center; }
      /* Botones apilados: en pantalla estrecha el secundario quedaría
         ilegible sobre la fotografía si comparte fila con el primario. */
      .hero-actions { display: grid; grid-template-columns: 1fr; }
      .hero-btn { justify-content: center; }
      .hero-copy { font-size: .76rem; }
    }
  `],
})
export class DashboardHeroComponent {
  @Input() firstName = '';
  @Input() businessName = '';
  @Input() imageUrl = 'dashboard-assets/banner-admin-tusala.png';
  @Input() tip: DashboardTip | null = null;
  @Input() loading = false;
  @Input() canCreateReservation = true;
  @Input() canManageSpaces = true;
  @Output() createReservation = new EventEmitter<void>();
  @Output() manageSpaces = new EventEmitter<void>();
  @Output() openTip = new EventEmitter<string>();

  readonly saludo = greeting();

  get resolvedImage(): string { return resolveDashboardImage(this.imageUrl, 'dashboard-assets/banner-admin-tusala.png'); }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (!img.src.endsWith('/dashboard-assets/banner-admin-tusala.png')) img.src = 'dashboard-assets/banner-admin-tusala.png';
  }
}
